import { useEffect, useState, useRef, useCallback } from 'react';
import { useUiStore } from '../../../store';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';

const SECTION_WIDTH = 235;
const GAP = 50;
const MOBILE_TIMELINE_PADDING = 32; // 16px on each side

export function useSectionsPagination({
  timelineItems,
  disableAutoAdvance = false,
  currentItemId = null,
}) {
  const sectionsListContainerRef = useRef();
  const sectionsListRef = useRef();
  const [sectionsContainerWidth, setSectionsContainerWidth] = useState(0);
  const [currentSectionsPage, setCurrentSectionsPage] = useState(0);
  const [sectionsPageCount, setSectionsPageCount] = useState(0);
  const prevItemsLengthRef = useRef(0); // Track previous items length to detect new additions
  const shouldAutoScrollRef = useRef(true);
  const autoScrollTimeoutRef = useRef(null);

  // Arrow-pagination related shared UI store values
  const isSidebarCollapsed = useUiStore((state) => state.isSidebarCollapsed);
  const setSectionsListTranslateX = useUiStore((state) => state.setSectionsListTranslateX);
  const isMobile = useCheckMobileScreen();

  // Read zoom from CSS variable and recompute widths on resize/sidebar changes
  useEffect(() => {
    const calculateSectionsContainerWidth = () => {
      // In mobile view, use actual container width if available, otherwise calculate
      if (isMobile && sectionsListContainerRef.current) {
        const containerWidth = sectionsListContainerRef.current.clientWidth;
        // Account for padding (16px on each side = 32px total)
        const width = containerWidth - MOBILE_TIMELINE_PADDING;
        setSectionsContainerWidth(Math.max(width, 0));
        return;
      }

      // Desktop calculation: use window width minus sidebar
      const zoomValue = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--zoom').trim()
      ) || 1;
      const sidebarWidth = (isSidebarCollapsed ? 126 : 373) * zoomValue;
      const width = window.innerWidth - sidebarWidth - 20 - 60; // match legacy calc
      setSectionsContainerWidth(width);
    };

    calculateSectionsContainerWidth();
    window.addEventListener('resize', calculateSectionsContainerWidth);
    return () => window.removeEventListener('resize', calculateSectionsContainerWidth);
  }, [isSidebarCollapsed, isMobile]);

  // In mobile view, observe container width changes using ResizeObserver
  // This ensures we get accurate width even if ref isn't ready on first render
  useEffect(() => {
    if (!isMobile || !sectionsListContainerRef.current) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const containerWidth = entry.contentRect.width;
        // Account for padding (16px on each side = 32px total)
        const width = containerWidth - MOBILE_TIMELINE_PADDING;
        setSectionsContainerWidth(Math.max(width, 0));
      });
    });

    resizeObserver.observe(sectionsListContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isMobile]);

  // Visible cards per page
  const zoom = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--zoom').trim()
  ) || 1;
  const zoomedSectionWidth = SECTION_WIDTH * zoom;
  const sectionsCount = Math.floor(sectionsContainerWidth / (zoomedSectionWidth + GAP)) || 1;

  // Total pages based on items plus Add Section button
  useEffect(() => {
    setSectionsPageCount(Math.max(timelineItems.length + 1 - sectionsCount, 0));
  }, [timelineItems.length, sectionsCount]);

  // Keep translateX in sync with current page
  useEffect(() => {
    setSectionsListTranslateX(currentSectionsPage * -(SECTION_WIDTH + GAP));
    return () => {
      // reset on unmount
      setSectionsListTranslateX(0);
    };
  }, [currentSectionsPage, setSectionsListTranslateX]);

  // Auto-advance to last page when a new item is added (first time after add)
  // Only enabled in editor mode (disabled in viewer mode)
  // Only auto-advance if items count INCREASED (new item added), not on initial load
  useEffect(() => {
    if (disableAutoAdvance) return;
    const currentItemsLength = timelineItems.length;
    // Only auto-advance if items count increased (new item added)
    // Skip if this is initial load (prevItemsLengthRef is 0 and currentItemsLength > 0)
    if (prevItemsLengthRef.current > 0 && currentItemsLength > prevItemsLengthRef.current) {
      // Move viewport to the end when a new item is added
      setCurrentSectionsPage(sectionsPageCount);
    }
    // Update ref for next comparison
    prevItemsLengthRef.current = currentItemsLength;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineItems.length, sectionsPageCount, disableAutoAdvance]);

  const scheduleAutoScrollReset = () => {
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
    }
    autoScrollTimeoutRef.current = setTimeout(() => {
      shouldAutoScrollRef.current = true;
    }, 600);
  };

  const scrollSections = (direction) => {
    shouldAutoScrollRef.current = false;
    if (direction === 'right') {
      if (currentSectionsPage === 0) return;
      setCurrentSectionsPage((prev) => prev - 1);
    } else {
      if (sectionsPageCount === currentSectionsPage) return;
      setCurrentSectionsPage((prev) => prev + 1);
    }
    scheduleAutoScrollReset();
  };

  const scrollCurrentItemIntoView = useCallback(
    (itemIndex) => {
      if (itemIndex < 0) return;

      const firstVisibleIndex = currentSectionsPage;
      const lastVisibleIndex = currentSectionsPage + sectionsCount - 1;

      if (itemIndex < firstVisibleIndex) {
        setCurrentSectionsPage(itemIndex);
      } else if (itemIndex > lastVisibleIndex) {
        const newPage = Math.min(
          Math.max(itemIndex - sectionsCount + 1, 0),
          sectionsPageCount,
        );
        setCurrentSectionsPage(newPage);
      }
    },
    [currentSectionsPage, sectionsCount, sectionsPageCount],
  );

  useEffect(() => {
    if (!currentItemId || !shouldAutoScrollRef.current) {
      return undefined;
    }

    const itemIndex = timelineItems.findIndex((item) => item.id === currentItemId);
    if (itemIndex === -1) {
      return undefined;
    }

    scrollCurrentItemIntoView(itemIndex);
    return undefined;
  }, [currentItemId, timelineItems, scrollCurrentItemIntoView]);

  useEffect(() => () => {
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
    }
  }, []);

  return {
    sectionsListContainerRef,
    sectionsListRef,
    sectionsContainerWidth,
    currentSectionsPage,
    setCurrentSectionsPage,
    sectionsPageCount,
    sectionsCount,
    scrollSections,
    scrollCurrentItemIntoView,
    SECTION_WIDTH,
    GAP,
  };
}

